import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { PostHogProvider } from "posthog-js/react";
import App from "@/frontend/app";
import { ErrorBoundary } from "@/frontend/components/ErrorBoundary";
import { setupGlobalErrorHandlers } from "@/frontend/lib/error-tracking";
import {
  captureProductEvent,
  getMarketingAppHandoff,
  getUrlWithoutMarketingHandoff,
  rememberMarketingHandoff,
} from "@/frontend/lib/product-analytics";

const rootElement = document.getElementById("root");

const posthogOptions = {
  api_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
  person_profiles: "identified_only" as const, // Only create profiles for identified users
  persistence: "localStorage+cookie" as const,
  cross_subdomain_cookie: true,
  cookieWinsOnConflict: true,
  capture_pageview: "history_change" as const,
  capture_pageleave: true, // Track when users leave pages
  capture_heatmaps: true, // Enable heatmap tracking
  autocapture: {
    dom_event_allowlist: ["click"], // Capture click events only
  },
  loaded: (posthog) => {
    if (import.meta.env.DEV) {
      console.log("PostHog loaded");
    }
    // Set up global error handlers after PostHog is loaded
    setupGlobalErrorHandlers();

    const handoff = getMarketingAppHandoff(document.referrer, window.location.href);
    if (handoff) {
      if (handoff.properties.handoff_id) {
        rememberMarketingHandoff(handoff.properties.handoff_id);
      }
      captureProductEvent(posthog, handoff);
    }

    const cleanedUrl = getUrlWithoutMarketingHandoff(window.location.href);
    if (cleanedUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(window.history.state, "", cleanedUrl);
    }
  },
};

// Register service worker for PWA and push notifications
if ("serviceWorker" in navigator) {
  if (import.meta.env.DEV) {
    // Development: Use simple service worker for push notifications only
    navigator.serviceWorker
      .register("/dev-sw.js", { type: "classic" })
      .then((registration) => {
        console.log("[Dev SW] Service Worker registered:", registration);
      })
      .catch((error) => {
        console.error("[Dev SW] Service Worker registration failed:", error);
      });
  } else {
    // Production: Load VitePWA registration module
    import("@/frontend/pwa-register");
  }
}

if (!rootElement?.innerHTML && rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <PostHogProvider
        apiKey={import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
        options={posthogOptions}
      >
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </PostHogProvider>
    </StrictMode>,
  );
}
